import React, { useRef } from 'react';
import { Calendar } from 'lucide-react';
import { theme } from '../../lib/theme';
import { FormLabel } from './FormLabel';

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  language?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  label,
  required,
  className = '',
  placeholder,
  disabled = false,
  language = 'en'
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString(language, { day: '2-digit', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    // On some desktop browsers, clicking the input area only focuses it.
    // showPicker() forces the native calendar dropdown to open.
    const inputEl = e.currentTarget;
    if (inputEl && 'showPicker' in HTMLInputElement.prototype) {
        try {
            inputEl.showPicker();
        } catch (error) {
            console.warn('showPicker failed', error);
        }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <FormLabel className={theme.typography.sectionLabel} isFilled={!!value}>
          {label}{required ? ' *' : ''}
        </FormLabel>
      )}
      <div className="relative group">
        {/* Styled display element (BEHIND the input) */}
        <div
          tabIndex={-1}
          aria-hidden="true"
          className={`w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-xl bg-white dark:bg-slate-900/40 flex items-center justify-between transition-all outline-none ${disabled ? 'opacity-60 cursor-not-allowed' : 'group-hover:border-blue-500/50 dark:group-hover:border-blue-400/50 group-active:bg-gray-50 dark:group-active:bg-white/5'}`}
        >
          <span className={`font-bold transition-colors ${!value ? 'text-gray-300 dark:text-white/20' : 'text-gray-900 dark:text-white'}`}>
            {value ? formatDate(value) : (placeholder || 'Izvēlieties datumu')}
          </span>
          <Calendar size={18} className={`${disabled ? 'text-gray-300' : 'text-gray-400 group-hover:text-blue-500'} transition-colors shrink-0`} />
        </div>

        {/* Native input (ON TOP but invisible) */}
        {!disabled && (
          <input
            ref={inputRef}
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onClick={handleInputClick}
            required={required}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-20"
            style={{ fontSize: '16px' }} // Prevents iOS zoom
          />
        )}
      </div>
    </div>
  );
};
